// Modern Orange, Gold & Black Theme - Contemporary Design System
export const lightTheme = {
  name: 'light',
  colors: {
    // Primary - Vibrant Orange
    primary: '#FF6B35',
    primaryHover: '#E85A28',
    primaryLight: '#FFF0EB',
    primaryGradient: 'linear-gradient(135deg, #FF6B35 0%, #FF8C42 50%, #FFB347 100%)',

    // Secondary - Rich Gold
    secondary: '#FFB347',
    secondaryHover: '#E69F30',
    secondaryLight: '#FFF8E7',
    secondaryGradient: 'linear-gradient(135deg, #FFB347 0%, #FFD700 100%)',

    // Accent - Deep Amber
    accent: '#F4A460',
    accentHover: '#D4853D',
    accentLight: '#FFF4E8',
    accentGradient: 'linear-gradient(135deg, #F4A460 0%, #FF6B35 100%)',

    // Signature Gradient (Orange to Gold to Black accent)
    brandGradient: 'linear-gradient(135deg, #FF6B35 0%, #FFB347 40%, #1A1A1A 100%)',
    heroGradient: 'linear-gradient(145deg, #FF6B35 0%, #FF8C42 25%, #FFB347 50%, #2D2D2D 100%)',
    glassGradient: 'linear-gradient(135deg, rgba(255,107,53,0.1) 0%, rgba(255,179,71,0.1) 100%)',

    // Background - Warm neutrals with subtle tint
    background: '#FAFAF8',
    backgroundSecondary: '#FFFFFF',
    backgroundTertiary: '#F5F3F0',
    backgroundDark: '#1A1A1A',

    // Surface
    surface: '#FFFFFF',
    surfaceHover: '#FDFCFB',
    surfaceGlass: 'rgba(255, 255, 255, 0.85)',
    surfaceElevated: '#FFFFFF',

    // Text
    text: '#1A1A1A',
    textSecondary: '#4A4A4A',
    textTertiary: '#7A7A7A',
    textInverse: '#FFFFFF',
    textGold: '#B8860B',
    gold: '#D4AF37',

    // Card
    cardBgGradient: 'linear-gradient(145deg, #FFFFFF 0%, #FAFAF8 100%)',

    // Border
    border: '#E8E5E1',
    borderLight: '#F2F0ED',
    borderDark: '#D4D1CC',
    borderGold: 'rgba(255, 179, 71, 0.3)',

    // Status
    success: '#22C55E',
    successLight: '#DCFCE7',
    warning: '#FFB347',
    warningLight: '#FEF3C7',
    error: '#EF4444',
    errorLight: '#FEE2E2',
    info: '#3B82F6',
    infoLight: '#DBEAFE',

    // Sidebar - Sleek Black with Orange accents
    sidebarBg: '#0F0F0F',
    sidebarBgGradient: 'linear-gradient(180deg, #0D0D0D 0%, #050505 25%, #000000 50%, #050505 75%, #0D0D0D 100%)',
    sidebarText: '#E5E5E5',
    sidebarTextMuted: '#888888',
    sidebarHover: '#252525',
    sidebarActive: '#FF6B35',
    sidebarActiveGradient: 'linear-gradient(90deg, #FF6B35 0%, #FF8C42 100%)',
    sidebarBorder: 'rgba(255, 107, 53, 0.2)',
  },

  // Glassmorphism effects
  glass: {
    background: 'rgba(255, 255, 255, 0.7)',
    backdropBlur: 'blur(20px)',
    border: '1px solid rgba(255, 255, 255, 0.3)',
  },

  spacing: {
    xs: '4px',
    sm: '8px',
    md: '16px',
    lg: '24px',
    xl: '32px',
    xxl: '48px',
    '2xl': '64px',
  },

  borderRadius: {
    sm: '6px',
    md: '12px',
    lg: '16px',
    xl: '24px',
    '2xl': '32px',
    full: '9999px',
  },

  shadows: {
    sm: '0 1px 3px 0 rgba(0, 0, 0, 0.08), 0 1px 2px -1px rgba(0, 0, 0, 0.08)',
    md: '0 4px 8px -2px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.06)',
    lg: '0 12px 24px -4px rgba(0, 0, 0, 0.12), 0 4px 8px -4px rgba(0, 0, 0, 0.08)',
    xl: '0 24px 48px -12px rgba(0, 0, 0, 0.18)',
    glow: '0 0 20px rgba(255, 107, 53, 0.3)',
    glowGold: '0 0 20px rgba(255, 179, 71, 0.3)',
    inner: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)',
    colored: '0 8px 24px -4px rgba(255, 107, 53, 0.25)',
  },

  typography: {
    fontFamily: "'Plus Jakarta Sans', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    fontFamilyHeading: "'Space Grotesk', 'Plus Jakarta Sans', -apple-system, sans-serif",
    fontFamilyMono: "'JetBrains Mono', 'Fira Code', monospace",
    fontSizes: {
      xs: '0.75rem',
      sm: '0.875rem',
      md: '1rem',
      lg: '1.125rem',
      xl: '1.25rem',
      '2xl': '1.5rem',
      '3xl': '2rem',
      '4xl': '2.5rem',
      '5xl': '3rem',
    },
    fontWeights: {
      light: 300,
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
      extrabold: 800,
    },
    lineHeights: {
      tight: 1.2,
      normal: 1.5,
      relaxed: 1.75,
    },
    letterSpacing: {
      tight: '-0.02em',
      normal: '0',
      wide: '0.02em',
    },
  },

  breakpoints: {
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
    '2xl': '1536px',
  },

  // Shared overlay stacking scale — every portalled/positioned overlay
  // (dropdowns, menus, modals, tooltips) must reference these tokens
  // instead of picking an ad-hoc number. See docs/03-architecture/app-shell.md.
  // Deliberate deviation: tooltip sits above modal (not below, as a naive
  // base<sticky<dropdown<tooltip<modal<toast reading would suggest) because
  // HelpTip renders inside form modals — a tooltip below modal z would clip
  // behind the very dialog it's meant to annotate.
  zIndex: {
    base: 0,
    sticky: 100,
    dropdown: 1000,
    modal: 1300,
    toast: 1400,
    tooltip: 1500,
  },

  transitions: {
    fast: '150ms cubic-bezier(0.4, 0, 0.2, 1)',
    normal: '250ms cubic-bezier(0.4, 0, 0.2, 1)',
    slow: '400ms cubic-bezier(0.4, 0, 0.2, 1)',
    spring: '500ms cubic-bezier(0.34, 1.56, 0.64, 1)',
    bounce: '600ms cubic-bezier(0.68, -0.55, 0.265, 1.55)',
  },

  // Animation presets
  animations: {
    fadeIn: 'fadeIn 0.3s ease-out',
    slideUp: 'slideUp 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    scaleIn: 'scaleIn 0.2s ease-out',
    shimmer: 'shimmer 2s infinite linear',
  },
};

export const darkTheme = {
  ...lightTheme,
  name: 'dark',
  colors: {
    // Primary - Vibrant Orange (brighter for dark mode)
    primary: '#FF7A4D',
    primaryHover: '#FF6B35',
    primaryLight: 'rgba(255, 107, 53, 0.15)',
    primaryGradient: 'linear-gradient(135deg, #FF6B35 0%, #FF8C42 50%, #FFB347 100%)',

    // Secondary - Rich Gold
    secondary: '#FFC857',
    secondaryHover: '#FFB347',
    secondaryLight: 'rgba(255, 179, 71, 0.15)',
    secondaryGradient: 'linear-gradient(135deg, #FFB347 0%, #FFD700 100%)',

    // Accent
    accent: '#FFAA5C',
    accentHover: '#F4A460',
    accentLight: 'rgba(244, 164, 96, 0.15)',
    accentGradient: 'linear-gradient(135deg, #F4A460 0%, #FF6B35 100%)',

    // Signature Gradients
    brandGradient: 'linear-gradient(135deg, #FF6B35 0%, #FFB347 40%, #0F0F0F 100%)',
    heroGradient: 'linear-gradient(145deg, #FF6B35 0%, #FF8C42 25%, #FFB347 50%, #1A1A1A 100%)',
    glassGradient: 'linear-gradient(135deg, rgba(255,107,53,0.08) 0%, rgba(255,179,71,0.08) 100%)',

    // Background - Deep blacks
    background: '#0A0A0A',
    backgroundSecondary: '#121212',
    backgroundTertiary: '#1A1A1A',
    backgroundDark: '#000000',

    // Surface
    surface: '#141414',
    surfaceHover: '#1C1C1C',
    surfaceGlass: 'rgba(20, 20, 20, 0.85)',
    surfaceElevated: '#1A1A1A',

    // Text
    text: '#F5F5F5',
    textSecondary: '#A0A0A0',
    textTertiary: '#666666',
    textInverse: '#1A1A1A',
    textGold: '#FFD700',
    gold: '#D4AF37',

    // Card
    cardBgGradient: 'linear-gradient(145deg, #1A1A1A 0%, #121212 100%)',

    // Border
    border: '#2A2A2A',
    borderLight: '#1F1F1F',
    borderDark: '#3A3A3A',
    borderGold: 'rgba(255, 179, 71, 0.25)',

    // Status
    success: '#4ADE80',
    successLight: 'rgba(74, 222, 128, 0.15)',
    warning: '#FFC857',
    warningLight: 'rgba(255, 200, 87, 0.15)',
    error: '#F87171',
    errorLight: 'rgba(248, 113, 113, 0.15)',
    info: '#60A5FA',
    infoLight: 'rgba(96, 165, 250, 0.15)',

    // Sidebar
    sidebarBg: '#0A0A0A',
    sidebarBgGradient: 'linear-gradient(180deg, #121212 0%, #0A0A0A 50%, #0F0F0F 100%)',
    sidebarText: '#E0E0E0',
    sidebarTextMuted: '#666666',
    sidebarHover: '#1A1A1A',
    sidebarActive: '#FF6B35',
    sidebarActiveGradient: 'linear-gradient(90deg, #FF6B35 0%, #FF8C42 100%)',
    sidebarBorder: 'rgba(255, 107, 53, 0.15)',
  },
  glass: {
    background: 'rgba(20, 20, 20, 0.8)',
    backdropBlur: 'blur(20px)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
  },
};

export type Theme = typeof lightTheme;

